import { Text, Stack, Group, Button, SegmentedControl, useMantineColorScheme } from '@mantine/core'
import { useUnitPrefs } from './_units'
import { useStations } from './_stations'
import { useI18n } from './_i18n'
import { TILE } from './stations/_tiles'

// One setting block: a label row (with an optional right-aligned action) and a
// full-width control. Full-width controls keep every row aligned to the same
// edges.
function Setting({ label, action, children }) {
  return (
    <div>
      <Group justify="space-between" align="center" wrap="nowrap" mb={6}>
        <Text fw={600} c="#fff">
          {label}
        </Text>
        {action}
      </Group>
      {children}
    </div>
  )
}

// SegmentedControls on the navy surface: a translucent-white track with a
// bright-blue (TILE.bright) active pill and white labels. `withItemsBorders`
// off drops the light separators that read as scratches on navy.
const SEG = {
  fullWidth: true,
  color: TILE.bright,
  withItemsBorders: false,
  styles: {
    root: { backgroundColor: 'rgba(255,255,255,0.08)' },
    label: { color: '#fff' },
    indicator: { boxShadow: '0 1px 3px rgba(0,0,0,0.35)' },
  },
}

// The settings controls (appearance, units, home-list toggles), shared by the
// standalone /settings page and the topnav settings overlay. All persisted in
// localStorage.
export default function SettingsContent() {
  const { t } = useI18n()
  const { prefs, setUnit } = useUnitPrefs()
  const { colorScheme, setColorScheme } = useMantineColorScheme()
  const {
    recentsEnabled,
    currentsEnabled,
    chartsEnabled,
    countriesMode,
    setPref,
    recents,
    clearRecents,
  } = useStations()
  const recentsCount = recents.tide.length + recents.current.length + recents.chart.length

  const showHide = [
    { label: t('show'), value: 'show' },
    { label: t('hide'), value: 'hide' },
  ]

  return (
    <Stack gap="lg">
      <Setting label={t('appearance')}>
        <SegmentedControl
          {...SEG}
          value={colorScheme}
          onChange={setColorScheme}
          data={[
            { label: t('appearance_system'), value: 'auto' },
            { label: t('appearance_light'), value: 'light' },
            { label: t('appearance_dark'), value: 'dark' },
          ]}
        />
      </Setting>

      <Setting label={t('tide_height')}>
        <SegmentedControl
          {...SEG}
          value={prefs.heightUnit}
          onChange={(v) => setUnit('heightUnit', v)}
          data={[
            { label: t('unit_feet'), value: 'ft' },
            { label: t('unit_meters'), value: 'm' },
          ]}
        />
      </Setting>

      <Setting label={t('distance')}>
        <SegmentedControl
          {...SEG}
          value={prefs.distanceUnit}
          onChange={(v) => setUnit('distanceUnit', v)}
          data={[
            { label: t('unit_miles'), value: 'mi' },
            { label: t('unit_kilometers'), value: 'km' },
          ]}
        />
      </Setting>

      <Setting label={t('temperature')}>
        <SegmentedControl
          {...SEG}
          value={prefs.tempUnit}
          onChange={(v) => setUnit('tempUnit', v)}
          data={[
            { label: t('unit_fahrenheit'), value: 'f' },
            { label: t('unit_celsius'), value: 'c' },
          ]}
        />
      </Setting>

      <Setting
        label={t('recent_stations')}
        action={
          <Button
            variant="subtle"
            size="compact-sm"
            color="ocean.2"
            disabled={recentsCount === 0}
            onClick={() => clearRecents()}
          >
            {t('clear_recents')}
          </Button>
        }
      >
        <SegmentedControl
          {...SEG}
          value={recentsEnabled ? 'show' : 'hide'}
          onChange={(v) => setPref('recentsEnabled', v === 'show')}
          data={showHide}
        />
      </Setting>

      <Setting label={t('currents')}>
        <SegmentedControl
          {...SEG}
          value={currentsEnabled ? 'show' : 'hide'}
          onChange={(v) => setPref('currentsEnabled', v === 'show')}
          data={showHide}
        />
      </Setting>

      <Setting label={t('nautical_charts')}>
        <SegmentedControl
          {...SEG}
          value={chartsEnabled ? 'show' : 'hide'}
          onChange={(v) => setPref('chartsEnabled', v === 'show')}
          data={showHide}
        />
      </Setting>

      {/* THREE WAY, and Auto is the default (Randy, 2026-09-12). Auto shows the
          list only when the page would otherwise be empty — no favourites, no
          recents, no location of any kind — so for most readers it means
          hidden. Always and Never are explicit and beat that rule in both
          directions, on every surface and across visits. */}
      <Setting label={t('browse_by_country')}>
        <SegmentedControl
          {...SEG}
          value={countriesMode}
          onChange={(v) => setPref('countriesMode', v)}
          data={[
            { value: 'auto', label: t('countries_auto') },
            { value: 'always', label: t('countries_always') },
            { value: 'never', label: t('countries_never') },
          ]}
        />
      </Setting>
    </Stack>
  )
}
